import { format } from 'date-fns';
import { th } from 'date-fns/locale';
import { ReportData } from '@/types/report';

/**
 * Format a date for display
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return format(date, 'dd MMM yyyy', { locale: th });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format a time for display in HH:mm format
 */
export const formatTime = (dateString: string): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return format(date, 'HH:mm');
  } catch (error) {
    console.error('Error formatting time:', error);
    return dateString;
  }
};

/**
 * Parse development report data from report
 * This attempts to extract structured data from the development_report field if available
 */
export const parseDevelopmentReport = (report: ReportData): Record<string, string> | null => {
  if (!report.development_report) {
    return null;
  }
  
  try {
    if (typeof report.development_report === 'string') {
      return JSON.parse(report.development_report);
    } else if (typeof report.development_report === 'object') {
      return report.development_report as Record<string, string>;
    }
  } catch (error) {
    console.error('Error parsing development report:', error);
  }
  
  return null;
};

/**
 * Get problems encountered from report
 */
export const getProblemsEncountered = (report: ReportData): string => {
  // First check if it's in the development_report field

  const developmentData = parseDevelopmentReport(report);
  
  try {
    if (developmentData && developmentData.problems_encountered) {
      let problemsEncountered = '';
      if (Array.isArray(developmentData.problems_encountered)) {
        for(const problem of developmentData.problems_encountered) {
          if (problem !== 'Other') {
            problemsEncountered += problem + ', ';
          } else {
            problemsEncountered += developmentData.problems_encountered_other;
          }
        }
      } else {
        problemsEncountered = developmentData.problems_encountered;
      }
      return problemsEncountered;
    } else {
      return '-'
    }
  } catch (error) {
    console.error('Error getting problems encountered:', error);
    return '-';
  }
};

/**
 * Get solutions applied from report
 */
export const getSolutionsApplied = (report: ReportData): string => {
  // First check if it's in the development_report field
  const developmentData = parseDevelopmentReport(report);
  
  try { 
    if (developmentData && developmentData.solutions_applied) {
    let solutionsApplied = '';
    if (Array.isArray(developmentData.solutions_applied)) {
      for(const solution of developmentData.solutions_applied) {
        if (solution !== 'Other') {
          solutionsApplied += solution + ', ';
        } else {
          solutionsApplied += developmentData.solutions_applied_other;
        }
      }
    } else {
      solutionsApplied = developmentData.solutions_applied;
    }
    return solutionsApplied;
  } else {
    return '-';
  }
  } catch (error) {
    console.error('Error getting solutions applied:', error);
    return '-';
  }
};

/**
 * Generate report text for display
 */
/**
 * Generate a formatted text report for sharing
 */
export const generateReportText = (report: ReportData | null): string => {
  if (!report) return '';

  // Get development report data
  const developmentData = parseDevelopmentReport(report);

  // Use start_date for class information if available
  const classDate = report.start_date ? formatDate(report.start_date) : formatDate(report.teaching_date);
  const classTime = report.start_date ? formatTime(report.start_date) : report.teaching_time_slot;
  const endTime = report.end_date ? formatTime(report.end_date) : '';
  
  // Get topic learned from either development report or direct field
  const topicLearned = developmentData?.topic_learned || report.topic_learned || '-';
  
  // Get ratings from either development report or direct fields
  const knowledgeRating = developmentData?.knowledge_rating || report.knowledge_rating || '-';
  const knowledgeBehavior = developmentData?.knowledge_behavior || report.knowledge_behavior || '-';
  const skillsRating = developmentData?.skills_rating || report.skills_rating || '-';
  const skillsBehavior = developmentData?.skills_behavior || report.skills_behavior || '-';
  const attitudeRating = developmentData?.attitude_rating || report.attitude_rating || '-';
  const attitudeBehavior = developmentData?.attitude_behavior || report.attitude_behavior || '-';
  const studentImprovement = developmentData?.student_improvement || report.student_improvement || '-';

  return `แจ้งพัฒนาการเรียนของ: น้อง${report.student_nickname} :${report.student_full_name}

📅 วันที่: ${classDate} เวลา ${classTime} - ${endTime}
📚 วิชา: ${report.subject}
👩‍🏫 ครูผู้สอน: ${report.teacher_display || report.teacher_id || '-'}
✏️ หัวข้อบทเรียน: ${topicLearned}
📌 ระยะเวลาเรียน: ${report.class_duration || '-'} ชั่วโมง
📌 ระดับพัฒนาการของน้องใน 3 มิติ

🔹 ระดับพัฒนาการมิติความรู้ (Knowledge): ${knowledgeRating}/5
- พฤติกรรมที่สะท้อนถึง: ${knowledgeBehavior}
🔹 ระดับพัฒนาการมิติทักษะ (Skills): ${skillsRating}/5
- พฤติกรรมที่สะท้อนถึง: ${skillsBehavior}
🔹 ระดับพัฒนาการทัศนคติ (Attitude): ${attitudeRating}/5
- พฤติกรรมที่สะท้อนถึง: ${attitudeBehavior}

📌 สำหรับวันนี้น้องมีพัฒนาการที่เพิ่มขึ้นคือ: ${studentImprovement}

📌 ปัญหาที่เกิดกับน้องในการเรียน: ${getProblemsEncountered(report)}

📌 แนวทางที่ใช้แก้ไขในคลาส: ${getSolutionsApplied(report)}

📌 การบ้าน / แบบฝึกหัดที่ให้น้องวันนี้: ${report.homework_assigned || report.homework_assignment || '-'}

📌 เรื่องถัดไปที่จะเรียน: ${report.next_topic || report.next_class_preparation || '-'}

รายงานฉบับนี้เป็นการสรุปพัฒนาการเรียนรู้ของน้องจากคุณครู เพื่อให้ผู้ปกครองสามารถติดตามความก้าวหน้าของบุตรหลานได้ หากมีข้อสงสัยเกี่ยวกับการเรียนหรืออยากพูดคุยเพิ่มเติม สามารถแจ้งครูได้เสมอนะคะ/ครับ 😊`;
};
